import { demoFunc } from './dees-harness-todos.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  cssManager,
  directives,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { IHarnessTodoItem } from '../interfaces.js';
import '../../00group-utility/dees-icon/dees-icon.js';

const { repeat } = directives;

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-todos': DeesHarnessTodos;
  }
}

/**
 * Agent task list: renders IHarnessTodoItem[] as a checklist. Used both as
 * the todo tool-card body and standalone (e.g. a "current tasks" sidebar
 * panel fed from the latest todowrite call). Keyed rows animate status
 * changes; completed items dim with a check, the in-progress item leads
 * with an accent marker.
 */
@customElement('dees-harness-todos')
export class DeesHarnessTodos extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor todos: IHarnessTodoItem[] = [];

  @property()
  accessor heading: string = '';

  /** tighter paddings for tool-card bodies */
  @property({ type: Boolean })
  accessor compact: boolean = false;

  @property({ type: Boolean })
  accessor showProgress: boolean = true;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
      }

      .headingRow {
        display: flex;
        align-items: baseline;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-xs) var(--dees-spacing-sm);
      }

      .headingText {
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size-sm, 12px);
        font-weight: 600;
      }

      .progressText {
        margin-left: auto;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size, 11px);
        font-variant-numeric: tabular-nums;
      }

      .list {
        display: block;
      }

      .item {
        display: flex;
        align-items: flex-start;
        gap: var(--dees-spacing-sm);
        padding: 3px var(--dees-spacing-xs);
        border-radius: var(--dees-radius-sm);
        corner-shape: var(--dees-corner-shape);
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size-sm, 12px);
        line-height: 1.45;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard);
      }

      :host([compact]) .item {
        padding: 2px 0;
      }

      .marker {
        flex: 0 0 14px;
        display: grid;
        place-items: center;
        width: 14px;
        height: 14px;
        margin-top: 2px;
        border-radius: var(--dees-radius-full);
      }

      .item.pending .marker {
        border: 1.5px solid var(--dees-color-border-strong);
      }

      .item.in_progress .marker {
        border: 1.5px solid var(--dees-color-accent-primary);
      }

      .item.in_progress .marker::after {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: var(--dees-radius-full);
        background: var(--dees-color-accent-primary);
        animation: harness-todo-active 1.4s ease-in-out infinite;
      }

      @keyframes harness-todo-active {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.45; transform: scale(0.75); }
      }

      .item.completed .marker {
        background: var(--dees-color-accent-success);
        color: #fff;
      }

      .item.in_progress .content {
        font-weight: 600;
      }

      .item.completed {
        opacity: 0.62;
      }

      .item.completed .content {
        text-decoration: line-through;
        text-decoration-color: var(--dees-color-text-muted);
      }

      .content {
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .empty {
        padding: var(--dees-spacing-sm) var(--dees-spacing-xs);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      @media (prefers-reduced-motion: reduce) {
        .item.in_progress .marker::after {
          animation: none;
        }
      }
    `,
  ];

  public get completedCount(): number {
    return this.todos.filter((todo) => todo.status === 'completed').length;
  }

  public render(): TemplateResult {
    return html`
      ${this.heading
        ? html`
            <div class="headingRow">
              <span class="headingText">${this.heading}</span>
              ${this.showProgress && this.todos.length
                ? html`<span class="progressText">${this.completedCount}/${this.todos.length}</span>`
                : ''}
            </div>
          `
        : ''}
      ${this.todos.length
        ? html`
            <div class="list" role="list">
              ${repeat(
                this.todos,
                (todo, index) => todo.id ?? `${index}-${todo.content}`,
                (todo) => html`
                  <div class="item ${todo.status}" role="listitem">
                    <span class="marker">
                      ${todo.status === 'completed'
                        ? html`<dees-icon icon="lucide:Check" iconSize="10"></dees-icon>`
                        : ''}
                    </span>
                    <span class="content">${todo.content}</span>
                  </div>
                `,
              )}
            </div>
          `
        : html`<div class="empty">No tasks yet.</div>`}
    `;
  }
}
